import { Combobox, Option, Tag, TagGroup } from "@fluentui/react-components";
import { ITag } from "@fluentui/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ColumnOptionsContext } from "./ColumnOptions";
import { Column, TaxonomyFilter as TaxonomyFilterType } from "./types";

type Props = {
  column: Column;
};

type OptionItem = { id: number; name: string };

const SEARCH_DEBOUNCE_MS = 300;

export const TaxonomySelectFilter = ({ column }: Props) => {
  const filter = column.filter as TaxonomyFilterType;
  const { taxonomy, setFilter, t, id } = useContext(ColumnOptionsContext);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<OptionItem[]>([]);
  // Names of items picked from server-side suggestions, until the taxonomy knows them.
  const [pickedNames, setPickedNames] = useState<Record<number, string>>({});
  const searchId = useRef(0);
  const isAsync = !!filter.onResolveSuggestions;

  const taxonomyOptions: OptionItem[] = (taxonomy[filter.taxonomyKey] || [])
    .filter((tax) => !filter.exclude || !filter.exclude.includes(tax.code))
    .map((tax) => ({
      id: tax.id,
      name: filter.customOptionLabel
        ? filter.customOptionLabel(tax)
        : t(tax.code),
    }));

  useEffect(() => {
    if (!filter.onResolveSuggestions) return;
    const currentSearch = ++searchId.current;
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      const result: ITag[] = await filter.onResolveSuggestions!(query);
      if (currentSearch !== searchId.current) return;
      setSuggestions(
        (result || []).map((tag) => ({ id: Number(tag.key), name: tag.name }))
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, filter.onResolveSuggestions]);

  const nameOf = (itemId: number) =>
    pickedNames[itemId] ??
    taxonomyOptions.find((o) => o.id === itemId)?.name ??
    String(itemId);

  const visibleOptions = (
    isAsync
      ? suggestions
      : taxonomyOptions.filter(
          (o) =>
            query === "" || o.name.toLowerCase().includes(query.toLowerCase())
        )
  ).filter((o) => !filter.value.includes(o.id));

  const handleRemoveItem = (itemId: number) => {
    setFilter(column.key, {
      ...filter,
      value: filter.value.filter((v) => v !== itemId),
    });
  };

  const handleSelectOption = (option: OptionItem) => {
    if (
      filter.maxOptionsSelect &&
      filter.value.length >= filter.maxOptionsSelect
    ) {
      return;
    }
    setPickedNames((prev) => ({ ...prev, [option.id]: option.name }));
    setFilter(column.key, {
      ...filter,
      value: [...filter.value, option.id],
    });
    setQuery("");
  };

  return (
    <div>
      {filter.value.length > 0 && (
        <TagGroupWrapper>
          <TagGroup>
            {filter.value.map((itemId) => (
              <Tag
                key={itemId}
                dismissible
                dismissIcon={{ onClick: () => handleRemoveItem(itemId) }}
                data-testid={`column-options-filter-tag-${column.key}-${itemId}`}
              >
                {nameOf(itemId)}
              </Tag>
            ))}
          </TagGroup>
        </TagGroupWrapper>
      )}
      <Combobox
        placeholder={column.placeholder ? t(column.placeholder) : undefined}
        id={id ? `${id}-taxonomySelect-input-${column.key}` : undefined}
        data-testid={`column-options-filter-input-${column.key}`}
        value={query}
        freeform
        style={{ width: "100%", minWidth: 0 }}
        onInput={(e) => setQuery(e.currentTarget.value)}
        onOptionSelect={(_, data) => {
          const option = visibleOptions.find(
            (o) => String(o.id) === data.optionValue
          );
          if (option) handleSelectOption(option);
        }}
      >
        {visibleOptions.map((option) => (
          <Option key={option.id} value={String(option.id)}>
            {option.name}
          </Option>
        ))}
      </Combobox>
    </div>
  );
};

const TagGroupWrapper = styled.div`
  margin-bottom: 8px;
`;
